/** @file Simplicity test for a closed ring: no fold, no two non-adjacent edges meet (O(n²)). */
import { flatOrient } from './flatOrient';

function within(p: ArrayLike<number>, a: number, b: number, c: number): boolean {
  const cx = p[2 * c] ?? 0;
  const cy = p[2 * c + 1] ?? 0;
  const ax = p[2 * a] ?? 0;
  const ay = p[2 * a + 1] ?? 0;
  const bx = p[2 * b] ?? 0;
  const by = p[2 * b + 1] ?? 0;
  return cx >= Math.min(ax, bx) && cx <= Math.max(ax, bx) && cy >= Math.min(ay, by) && cy <= Math.max(ay, by);
}

function touchesAt(p: ArrayLike<number>, a: number, b: number, c: number): boolean {
  return flatOrient(p, a, b, c) === 0 && within(p, a, b, c);
}

function edgesMeet(p: ArrayLike<number>, a: number, b: number, c: number, d: number): boolean {
  const o1 = flatOrient(p, a, b, c);
  const o2 = flatOrient(p, a, b, d);
  const o3 = flatOrient(p, c, d, a);
  const o4 = flatOrient(p, c, d, b);
  if (o1 * o2 < 0 && o3 * o4 < 0) return true;
  return touchesAt(p, a, b, c) || touchesAt(p, a, b, d) || touchesAt(p, c, d, a) || touchesAt(p, c, d, b);
}

/** True when the corner a→b→c folds back on itself: collinear, and b→c does not continue a→b (or has length 0). */
function foldsAt(p: ArrayLike<number>, a: number, b: number, c: number): boolean {
  if (flatOrient(p, a, b, c) !== 0) return false;
  const bx = p[2 * b] ?? 0;
  const by = p[2 * b + 1] ?? 0;
  return (bx - (p[2 * a] ?? 0)) * ((p[2 * c] ?? 0) - bx) + (by - (p[2 * a + 1] ?? 0)) * ((p[2 * c + 1] ?? 0) - by) <= 0;
}

/** True when some corner of the n-point ring folds back (a collinear 3-point ring always does). */
function hasFold(ring: ArrayLike<number>, n: number): boolean {
  for (let i = 0; i < n; i++) if (foldsAt(ring, (i + n - 1) % n, i, (i + 1) % n)) return true;
  return false;
}

/**
 * True when the ring has ≥ 3 points, no corner folds back (adjacent edges overlapping, a repeated point) and no
 * pair of non-adjacent edges meets (shared end points of neighbours are fine); such a ring has a non-zero area.
 */
export function ringIsSimple(ring: ArrayLike<number>): boolean {
  const n = ring.length >> 1;
  if (n < 3 || hasFold(ring, n)) return false;
  for (let i = 0; i < n; i++) {
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue;
      if (edgesMeet(ring, i, i + 1, j, (j + 1) % n)) return false;
    }
  }
  return true;
}
